import { q } from '@engineer-os/engine';
import { vmError } from './errors.js';
import { CWD_FILE, WORKSPACE_ROOT } from './types.js';
import { FORGET_STAMP } from './worksave.js';
/**
 * The browser's workspace driver: DockerWorkspace with `docker exec` swapped
 * for the guest agent on ttyS1.
 *
 * Nearly all of it is server/src/workspace/docker.ts verbatim, and
 * deliberately so — `readFile` is one `cat`, `writeFile` is one `cat >` with
 * the content on stdin and the path in an environment variable, `listFiles`
 * is one `find -printf` with NUL-terminated records — because the editor's
 * file tree, the grader and the author tests must see the same workspace
 * whichever driver is under them. Drift here corrupts the file tree
 * silently.
 *
 * What is not the same:
 *   - there is one guest and one workspace, not a container per session, so
 *     `destroy` empties /workspace instead of removing a machine;
 *   - the learner's shell is already running on ttyS0 under inittab, so
 *     `spawnShell` attaches to it rather than starting one;
 *   - a large write is several `cat`s rather than one. `docker exec` has a
 *     pipe under it; this driver has a serial line with no flow control,
 *     which one oversized request kills outright (`MAX_REQUEST_BYTES`).
 */
/** Default cap on a single listing, so a runaway tree cannot flood the client. */
const DEFAULT_MAX_ENTRIES = 500;
/** Hard cap on the bytes `find` may emit, in case the tree is enormous. */
const LIST_MAX_BYTES = 256 * 1024;
/**
 * One `find` per listing. Records are NUL-terminated and the three fields are
 * separated by US (\037), so paths containing spaces, quotes or newlines
 * survive the round trip. `.git`, `node_modules` and dotfile directories are
 * pruned, matching the local driver. The image installs GNU findutils and
 * coreutils, so `-printf` and `head -c` are available.
 */
const LIST_CMD = `find . -mindepth 1 \\( -name .git -o -name node_modules -o \\( -type d -name '.*' \\) \\) -prune` +
    ` -o -printf '%y\\037%s\\037%P\\0' | head -c ${LIST_MAX_BYTES}`;
/**
 * One `cat` per write. The path travels as an environment variable and the
 * content over stdin, so neither is ever parsed by the shell: content full of
 * quotes, backticks, `$(...)` and newlines is written verbatim.
 */
const WRITE_CMD = 'mkdir -p "$(dirname "$EOS_WRITE_PATH")" && cat > "$EOS_WRITE_PATH"';
/**
 * Content per request when a file needs more than one.
 *
 * The editor accepts a file up to `MAX_EDIT_BYTES` (512 KiB), and the whole
 * of one used to travel as a single line on ttyS1, where anything much past
 * 96 KiB stops the guest permanently (`MAX_REQUEST_BYTES`). So a large file
 * goes as several requests: this much content is about 44 KB of base64 once
 * framed, a third of the largest line measured safe, and each request only
 * leaves the host once the agent has answered the last — which is the flow
 * control the serial line itself has none of. It costs a round trip per
 * chunk (about 1.2 s in the guest for this size, most of it the bytes), and
 * only for files past 32 KiB; every source file a level ships is one
 * request, exactly as it was.
 */
const WRITE_CHUNK_BYTES = 32 * 1024;
/**
 * A chunked write lands in a sibling file and is renamed at the end, so a
 * write that fails halfway leaves the learner's file as it was rather than
 * truncated to whatever arrived. `mv` within a directory is atomic, and it
 * rides on the last chunk's request rather than costing one of its own.
 */
const WRITE_PART_CMD = 'mkdir -p "$(dirname "$EOS_WRITE_PATH")" && cat > "$EOS_WRITE_PATH.eos-part"';
const APPEND_PART_CMD = 'cat >> "$EOS_WRITE_PATH.eos-part"';
const FINISH_PART_CMD = 'cat >> "$EOS_WRITE_PATH.eos-part" && mv "$EOS_WRITE_PATH.eos-part" "$EOS_WRITE_PATH"';
const DISCARD_PART_CMD = 'rm -f "$EOS_WRITE_PATH.eos-part"';
const encoder = new TextEncoder();
const decoder = new TextDecoder();
/**
 * Split text into pieces of at most `maxBytes` UTF-8 bytes, never through a
 * character.
 *
 * The wire measures bytes and JavaScript measures UTF-16 code units, and the
 * two differ by up to 3× on text a learner can easily write (a file of CJK
 * or emoji), so the split is made on the encoded bytes and each piece
 * decoded back. Cutting mid-character would hand the guest a byte sequence
 * that decodes to U+FFFD on both sides of the seam — a save that silently
 * corrupts the file it saved.
 */
function chunkUtf8(text, maxBytes) {
    const bytes = encoder.encode(text);
    if (bytes.length <= maxBytes)
        return [text];
    const chunks = [];
    for (let i = 0; i < bytes.length;) {
        let end = Math.min(bytes.length, i + maxBytes);
        // 10xxxxxx is a continuation byte: back up to the character's first one.
        while (end < bytes.length && (bytes[end] & 0xc0) === 0x80)
            end -= 1;
        chunks.push(decoder.decode(bytes.subarray(i, end)));
        i = end;
    }
    return chunks;
}
/** Empty the workspace, dotfiles included, without removing the mount point itself. */
const WIPE_CMD = `${FORGET_STAMP}; rm -rf ${WORKSPACE_ROOT}/* ${WORKSPACE_ROOT}/.[!.]* 2>/dev/null; true`;
/** Directories before files at each level, then alphabetically (the local driver's order). */
function compareFiles(a, b) {
    const as = a.path.split('/');
    const bs = b.path.split('/');
    const n = Math.min(as.length, bs.length);
    for (let i = 0; i < n; i++) {
        if (as[i] === bs[i])
            continue;
        // A segment that is not the last one of its path is necessarily a directory.
        const aDir = i < as.length - 1 || a.dir;
        const bDir = i < bs.length - 1 || b.dir;
        if (aDir !== bDir)
            return aDir ? -1 : 1;
        return as[i] < bs[i] ? -1 : 1;
    }
    return as.length - bs.length; // a directory sorts before everything inside it
}
/**
 * One VM, many workspaces over its life: `SessionManager.reset` destroys a
 * workspace and asks the driver for another, and in the browser both are the
 * same /workspace on the same guest.
 */
export class BrowserDriver {
    vm;
    name = 'browser';
    constructor(vm) {
        this.vm = vm;
    }
    async create(id) {
        return new BrowserWorkspace(this.vm, id);
    }
}
export class BrowserWorkspace {
    vm;
    id;
    driver = 'browser';
    root = WORKSPACE_ROOT;
    destroyed = false;
    /**
     * Where the login shell on ttyS0 is running, as last written to
     * /run/eos/cwd. The seeder writes it and restarts the shell as part of
     * loading a level, so `spawnShell` only has to restart it when it is asked
     * for a different directory.
     */
    shellCwd = WORKSPACE_ROOT;
    constructor(vm, id) {
        this.vm = vm;
        this.id = id;
    }
    /** A workspace-relative path, as the guest sees it. */
    abs(relPath) {
        return relPath ? `${this.root}/${relPath}` : this.root;
    }
    /**
     * Record that the shell on ttyS0 is now running in `dir`, which is what
     * `TarballSeeder` does to /run/eos/cwd as part of loading a level.
     */
    shellStartedIn(dir) {
        this.shellCwd = dir;
    }
    exec(cmd, opts = {}) {
        if (this.destroyed)
            return Promise.reject(vmError('destroyed', 'this workspace was destroyed'));
        const dir = this.abs(opts.cwd);
        // The guest agent falls back to /workspace when the requested directory
        // does not exist, which would quietly run a listing or a check against
        // the wrong tree. The Docker driver fails instead, so this one does too.
        const script = `cd ${q(dir)} 2>/dev/null || { echo ${q(`no such directory: ${dir}`)} >&2; exit 1; }\n${cmd}`;
        return this.vm.exec(script, { cwd: dir, input: opts.input, env: opts.env, timeoutMs: opts.timeoutMs });
    }
    /**
     * The wire already carries the script as a file the agent runs with bash,
     * so piping into `bash -s` and running a command are the same request.
     */
    runScript(script, opts = {}) {
        return this.exec(script, { ...opts, timeoutMs: opts.timeoutMs ?? 60_000 });
    }
    /**
     * Attach to the learner's shell, which inittab has been respawning on
     * ttyS0 since the machine booted.
     *
     * There is exactly one, so this is an attachment rather than a spawn: the
     * handle's `kill` ends the shell and inittab starts another in the
     * directory /run/eos/cwd names, which is how a level change moves the
     * prompt. `onExit` is therefore never called — the shell always comes
     * back — and the terminal gateway above this never waited for it.
     */
    spawnShell(cwd, cols, rows) {
        const dir = this.abs(cwd);
        const listeners = new Set();
        const handle = this.vm.attachTerminal({
            onData: (data) => {
                for (const cb of [...listeners])
                    cb(data);
            },
        });
        if (dir !== this.shellCwd)
            this.restartShellIn(dir);
        handle.resize(cols, rows);
        return {
            write: (data) => handle.write(data),
            resize: (c, r) => handle.resize(c, r),
            onData: (cb) => void listeners.add(cb),
            // Nothing to report: killing the shell makes inittab respawn it.
            onExit: () => undefined,
            kill: () => {
                handle.detach();
                listeners.clear();
                void this.vm.exec('pkill -HUP -t ttyS0; true').catch(() => undefined);
            },
        };
    }
    /**
     * Point the next ttyS0 login shell at `dir` and restart it there.
     *
     * SIGHUP rather than pkill's default SIGTERM: an interactive bash ignores
     * SIGTERM, so the default leaves the old shell running in the old
     * directory (seeder.ts says the same, and it is the one bug of this kind
     * that a learner meets on their first command).
     */
    restartShellIn(dir) {
        this.shellCwd = dir;
        // Fire and forget: it is queued on the same channel as everything else,
        // so it happens in order, and a terminal that came back in the wrong
        // directory is not worth failing an attach over.
        void this.vm.exec(`echo ${q(dir)} > ${CWD_FILE}; pkill -HUP -t ttyS0; true`).catch(() => undefined);
    }
    async readFile(relPath) {
        const r = await this.exec(`cat ${q(relPath)}`);
        return r.code === 0 ? r.stdout : null;
    }
    async listFiles(dir, opts = {}) {
        const max = opts.maxEntries ?? DEFAULT_MAX_ENTRIES;
        const r = await this.exec(LIST_CMD, { cwd: dir || undefined });
        if (r.code !== 0)
            return []; // missing or unreadable directory
        const records = r.stdout.split('\0');
        records.pop(); // the empty tail after the final NUL, or a byte-cap-truncated record
        const files = [];
        for (const rec of records) {
            const [type, size, ...rest] = rec.split('\x1f');
            const relPath = rest.join('\x1f'); // paths may legitimately contain US
            if (!relPath || (type !== 'd' && type !== 'f'))
                continue; // skip symlinks, sockets, …
            files.push({ path: relPath, dir: type === 'd', size: type === 'd' ? 0 : Number(size) || 0 });
        }
        // find walks in filesystem order, so sort here; the entry cap is applied
        // afterwards, which for mission-sized repositories is the same set the
        // local driver returns.
        return files.sort(compareFiles).slice(0, max);
    }
    /**
     * One `cat` for a file that fits on the wire, and a series of appends into
     * a temporary sibling for one that does not (see `WRITE_CHUNK_BYTES`).
     */
    async writeFile(relPath, content) {
        const env = { EOS_WRITE_PATH: relPath };
        const chunks = chunkUtf8(content, WRITE_CHUNK_BYTES);
        const check = (r) => {
            if (r.code !== 0)
                throw new Error(`failed to write "${relPath}": ${r.stderr.trim() || `exit ${r.code}`}`);
        };
        if (chunks.length === 1) {
            check(await this.exec(WRITE_CMD, { input: chunks[0], env }));
            return;
        }
        try {
            for (let i = 0; i < chunks.length; i++) {
                const cmd = i === 0 ? WRITE_PART_CMD : i === chunks.length - 1 ? FINISH_PART_CMD : APPEND_PART_CMD;
                check(await this.exec(cmd, { input: chunks[i], env }));
            }
        }
        catch (err) {
            // Leave neither a truncated file nor a part file: the second would show
            // up in the editor's tree as something the learner never made.
            await this.exec(DISCARD_PART_CMD, { env }).catch(() => undefined);
            throw err;
        }
    }
    /**
     * The VM outlives the workspace, so this empties /workspace rather than
     * destroying anything. Seeding the next level empties it again, which is
     * the point: a reset and a level change are the same operation.
     */
    async destroy() {
        if (this.destroyed)
            return;
        this.destroyed = true;
        await this.vm.exec(WIPE_CMD, { timeoutMs: 30_000 }).catch(() => undefined);
    }
}
//# sourceMappingURL=workspace.js.map